import { useState, useEffect, useCallback, type ReactNode } from 'react';
import { formatDateTime } from '../lib/dates';
import { api, ApiError, asList } from '../lib/api';
import { DataTable, type Column } from '../components/DataTable';

/* ============================================================================
   THE HOSTEL

   The school is legally responsible for a child overnight. So the first thing a
   warden sees is not a room list — it's WHO IS MISSING.

   Everything else on this screen is subordinate to that.
   ========================================================================== */

interface Outpass {
  id: string; studentId: string; studentName: string; admissionNumber: string;
  kind: string; reason: string; destination: string | null; accompaniedBy: string | null;
  expectedOutAt: string; expectedBackAt: string;
  status: string; parentApproved: boolean; wardenApproved: boolean;
  actualOutAt: string | null; actualBackAt: string | null;
  isOverdue: boolean; overdueMinutes: number;
  guardianMobile: string | null; rejectedReason: string | null;
}
interface Dashboard {
  residentCount: number; currentlyOut: number; overdueCount: number;
  visitorsInside: number; pendingApprovals: number;
  overdue: Outpass[];
}
interface Bed { id: string; label: string; occupantStudentId: string | null; occupantName: string | null; isUsable: boolean; }
interface Room { id: string; roomNumber: string; floor: string | null; totalBeds: number; occupiedBeds: number; freeBeds: number; beds: Bed[]; }
interface Block { id: string; name: string; gender: string; totalBeds: number; occupiedBeds: number; freeBeds: number; rooms: Room[]; }
interface Resident { studentId: string; fullName: string; admissionNumber: string; classLabel: string | null; }

type Tab = 'tonight' | 'gate' | 'rooms' | 'visitors';

export function HostelPage() {
  const [tab, setTab] = useState<Tab>('tonight');

  return (
    <div>
      <div className="page-head">
        <div>
          <div className="eyebrow" style={{ marginBottom: 6 }}>Residential</div>
          <h1 className="h-display" style={{ fontSize: 28 }}>Hostel</h1>
        </div>
      </div>

      <div className="tab-strip" style={{ gap: 4, marginBottom: 18 }}>
        <TabBtn on={tab === 'tonight'} onClick={() => setTab('tonight')}>Tonight</TabBtn>
        <TabBtn on={tab === 'gate'} onClick={() => setTab('gate')}>The gate</TabBtn>
        <TabBtn on={tab === 'rooms'} onClick={() => setTab('rooms')}>Rooms &amp; beds</TabBtn>
        <TabBtn on={tab === 'visitors'} onClick={() => setTab('visitors')}>Visitors</TabBtn>
      </div>

      {tab === 'tonight' && <Tonight />}
      {tab === 'gate' && <Gate />}
      {tab === 'rooms' && <Rooms />}
      {tab === 'visitors' && <Visitors />}
    </div>
  );
}

/* ------------------------------------------------------------------ tonight */

function Tonight() {
  const [dash, setDash] = useState<Dashboard | null>(null);

  const load = useCallback(() => {
    // Loading this SWEEPS for overdue students — so simply opening the app checks
    // whether anyone is missing, and alerts their parents.
    api.get<Dashboard>('/api/hostel/dashboard').then(setDash).catch(() => setDash(null));
  }, []);

  useEffect(() => {
    load();
    // Keep sweeping while the warden has the screen open.
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, [load]);

  if (!dash) {
    return (
      <div className="card" style={{ padding: 20 }}>
        <span className="sk" style={{ width: '40%', marginBottom: 10 }} />
        <span className="sk" style={{ width: '70%' }} />
      </div>
    );
  }

  return (
    <div>
      {/* ⚠ THE THING THAT MATTERS. Impossible to miss, by design. */}
      {dash.overdueCount > 0 && (
        <div className="card" style={{
          padding: 20, marginBottom: 20,
          borderColor: 'var(--brand)', borderWidth: 2,
          background: 'color-mix(in srgb, var(--brand) 8%, var(--surface))',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <span style={{ fontSize: 22 }} aria-hidden="true">⚠</span>
            <h2 className="h-display" style={{ fontSize: 20, color: 'var(--brand)' }}>
              {dash.overdueCount} student{dash.overdueCount === 1 ? ' has' : 's have'} not returned
            </h2>
          </div>

          {dash.overdue.map((o) => (
            <div key={o.id} className="card" style={{ padding: 14, marginBottom: 8 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
                <strong style={{ fontSize: 16 }}>{o.studentName}</strong>
                <span className="mono" style={{ fontSize: 12, color: 'var(--ink-3)' }}>{o.admissionNumber}</span>
                <strong style={{ color: 'var(--brand)', marginLeft: 'auto' }}>
                  {formatLate(o.overdueMinutes)} late
                </strong>
              </div>
              <div style={{ fontSize: 13, color: 'var(--ink-2)', marginTop: 4 }}>
                Due back {formatDateTime(o.expectedBackAt)} · {o.reason}
                {o.destination && ` · ${o.destination}`}
              </div>
              {/* The parent's number, right there. A warden should not be hunting for it at 10pm. */}
              {o.guardianMobile && (
                <a href={`tel:${o.guardianMobile}`} className="btn btn-accent"
                  style={{ marginTop: 10, textDecoration: 'none' }}>
                  📞 Call guardian — {o.guardianMobile}
                </a>
              )}
              <p style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 8 }}>
                The parent has been notified automatically.
              </p>
            </div>
          ))}
        </div>
      )}

      <div className="kpis">
        <Kpi label="Residents" value={dash.residentCount} hint="in the hostel" />
        <Kpi label="Currently out" value={dash.currentlyOut} hint="signed out on a pass" />
        <Kpi label="Overdue" value={dash.overdueCount} hint="not back yet" danger={dash.overdueCount > 0} />
        <Kpi label="Awaiting approval" value={dash.pendingApprovals} hint="outpass requests" />
      </div>

      {dash.overdueCount === 0 && (
        <div className="card" style={{ padding: 24, textAlign: 'center' }}>
          <strong style={{ fontSize: 16, color: 'var(--ok-ink, #1e7d4f)' }}>
            ✓ Everyone is accounted for
          </strong>
          <p style={{ fontSize: 13.5, color: 'var(--ink-2)', marginTop: 4 }}>
            {dash.currentlyOut > 0
              ? `${dash.currentlyOut} student(s) are out on an approved pass and not yet due back.`
              : 'No students are currently out.'}
          </p>
        </div>
      )}
    </div>
  );
}

function Kpi({ label, value, hint, danger }: { label: string; value: number; hint: string; danger?: boolean }) {
  return (
    <div className="card" style={{ padding: 16, borderColor: danger ? 'var(--brand)' : undefined }}>
      <div style={{ fontSize: 11, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '.04em' }}>{label}</div>
      <div style={{
        fontSize: 28, fontWeight: 700, fontFamily: 'var(--font-display)',
        color: danger && value > 0 ? 'var(--brand)' : undefined,
      }}>{value}</div>
      <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>{hint}</div>
    </div>
  );
}

/* --------------------------------------------------------------------- gate */

function Gate() {
  const [rows, setRows] = useState<Outpass[] | null>(null);
  const [filter, setFilter] = useState<string>('');
  const [requesting, setRequesting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setRows(null);
    const q = filter ? `?status=${filter}` : '';
    api.get<Outpass[]>(`/api/hostel/outpasses${q}`).then(setRows).catch(() => setRows([]));
  }, [filter]);
  useEffect(load, [load]);

  const act = async (fn: () => Promise<unknown>) => {
    setError(null);
    try { await fn(); load(); }
    catch (e) { setError(e instanceof ApiError ? e.message : 'That didn’t work.'); }
  };

  const columns: Column<Outpass>[] = [
    {
      id: 'student', header: 'Student', primary: true, sortable: true,
      value: (o) => o.studentName, cell: (o) => o.studentName,
    },
    {
      id: 'reason', header: 'Reason', hideMd: true,
      value: (o) => o.reason,
      cell: (o) => <span style={{ fontSize: 13 }}>{o.reason}</span>,
    },
    {
      id: 'back', header: 'Due back', sortable: true,
      value: (o) => o.expectedBackAt,
      cell: (o) => (
        <span style={{ color: o.isOverdue ? 'var(--brand)' : undefined, fontWeight: o.isOverdue ? 600 : 400 }}>
          {new Date(o.expectedBackAt).toLocaleString([], { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
        </span>
      ),
    },
    {
      id: 'approvals', header: 'Approvals', hideSm: true,
      value: (o) => `${o.parentApproved}${o.wardenApproved}`,
      cell: (o) => (
        <span style={{ fontSize: 12 }}>
          <span style={{ color: o.parentApproved ? 'var(--ok-ink, #1e7d4f)' : 'var(--ink-3)' }}>
            {o.parentApproved ? '✓' : '○'} Parent
          </span>
          {' · '}
          <span style={{ color: o.wardenApproved ? 'var(--ok-ink, #1e7d4f)' : 'var(--ink-3)' }}>
            {o.wardenApproved ? '✓' : '○'} Warden
          </span>
        </span>
      ),
    },
    {
      id: 'status', header: 'Status',
      value: (o) => o.status,
      cell: (o) => <StatusPill o={o} />,
    },
    {
      id: 'act', header: '', actions: true,
      cell: (o) => (
        <div style={{ display: 'flex', gap: 6 }}>
          {(o.status === 'Requested' || o.status === 'ParentApproved') && (
            <button className="btn btn-accent" style={{ padding: '4px 10px', fontSize: 12 }}
              onClick={() => void act(() => api.post(`/api/hostel/outpasses/${o.id}/warden-approve`, {}))}>
              Approve
            </button>
          )}
          {o.status === 'Approved' && (
            <button className="btn btn-accent" style={{ padding: '4px 10px', fontSize: 12 }}
              onClick={() => void act(() => api.post(`/api/hostel/outpasses/${o.id}/sign-out`, {}))}>
              Sign out
            </button>
          )}
          {o.status === 'Out' && (
            <button className="btn btn-accent" style={{ padding: '4px 10px', fontSize: 12 }}
              onClick={() => void act(() => api.post(`/api/hostel/outpasses/${o.id}/sign-in`, {}))}>
              Sign back in
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div>
      {error && <div className="pill pill-danger" style={{ padding: '9px 12px', borderRadius: 8, marginBottom: 12, justifyContent: 'flex-start' }}>{error}</div>}

      <div style={{ display: 'flex', marginBottom: 12 }}>
        <button className="btn btn-accent" style={{ marginLeft: 'auto' }} onClick={() => setRequesting(true)}>
          + New outpass
        </button>
      </div>

      <DataTable
        rows={rows}
        columns={columns}
        rowKey={(o) => o.id}
        search
        searchPlaceholder="Search by student…"
        filters={
          <>
            {['', 'Requested', 'Approved', 'Out', 'Returned'].map((s) => (
              <button key={s || 'all'} className={`chip ${filter === s ? 'is-on' : ''}`} onClick={() => setFilter(s)}>
                {s || 'All'}
              </button>
            ))}
          </>
        }
        empty={{ title: 'No outpasses', body: 'Outpass requests will appear here.' }}
      />

      {requesting && <OutpassDialog onClose={() => setRequesting(false)} onDone={() => { setRequesting(false); load(); }} />}
    </div>
  );
}

function StatusPill({ o }: { o: Outpass }) {
  if (o.isOverdue) {
    return <span className="pill pill-danger" style={{ fontSize: 11 }}>⚠ Overdue</span>;
  }
  const cls =
    o.status === 'Out' ? 'pill-warn'
      : o.status === 'Returned' ? 'pill-ok'
        : o.status === 'Rejected' ? 'pill-danger'
          : 'pill-muted';
  return <span className={`pill ${cls}`} style={{ fontSize: 11 }}>{o.status}</span>;
}

function OutpassDialog({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Resident[]>([]);
  const [picked, setPicked] = useState<Resident | null>(null);
  const [f, setF] = useState({
    kind: 'DayOut', reason: '', destination: '', accompaniedBy: '',
    outAt: '', backAt: '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (query.trim().length < 2) { setResults([]); return; }
    const t = setTimeout(() => {
      api.get<Resident[]>(`/api/hostel/students?search=${encodeURIComponent(query)}`)
        .then((r) => setResults(asList(r))).catch(() => setResults([]));
    }, 250);
    return () => clearTimeout(t);
  }, [query]);

  const save = async () => {
    if (!picked || !f.reason.trim() || !f.outAt || !f.backAt) return;
    setBusy(true); setError(null);
    try {
      await api.post('/api/hostel/outpasses', {
        studentId: picked.studentId,
        kind: f.kind,
        reason: f.reason.trim(),
        destination: f.destination.trim() || null,
        accompaniedBy: f.accompaniedBy.trim() || null,
        expectedOutAt: new Date(f.outAt).toISOString(),
        expectedBackAt: new Date(f.backAt).toISOString(),
      });
      onDone();
    } catch (e) { setError(e instanceof ApiError ? e.message : 'Could not save.'); }
    finally { setBusy(false); }
  };

  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <div className="sheet" onClick={(e) => e.stopPropagation()}>
        <h2 className="h-display" style={{ fontSize: 20, marginBottom: 4 }}>New outpass</h2>
        <p style={{ color: 'var(--ink-2)', fontSize: 13, marginBottom: 14 }}>
          The parent will be asked to approve before the student can leave.
        </p>

        {error && <div className="pill pill-danger" style={{ padding: '8px 12px', borderRadius: 8, marginBottom: 12, justifyContent: 'flex-start' }}>{error}</div>}

        <label className="label">Student</label>
        <input className="input" value={picked ? picked.fullName : query}
          placeholder="Search by name or admission number…"
          onChange={(e) => { setPicked(null); setQuery(e.target.value); }} />

        {!picked && results.length > 0 && (
          <div className="card" style={{ marginTop: 4, padding: 0, overflow: 'hidden' }}>
            {results.map((r) => (
              <button key={r.studentId} onClick={() => { setPicked(r); setQuery(''); }}
                style={{
                  display: 'flex', gap: 8, width: '100%', padding: '9px 12px', textAlign: 'left',
                  background: 'none', border: 'none', borderBottom: '1px solid var(--line-2)', cursor: 'pointer',
                }}>
                <span>{r.fullName}</span>
                <span className="mono" style={{ fontSize: 11, color: 'var(--ink-3)', marginLeft: 'auto' }}>
                  {r.admissionNumber}
                </span>
              </button>
            ))}
          </div>
        )}

        {picked && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 12 }}>
            <div>
              <label className="label">Kind</label>
              <select className="input" value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })}>
                <option value="DayOut">Day out</option>
                <option value="Home">Going home</option>
                <option value="Medical">Medical</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div><label className="label">Reason</label>
              <input className="input" value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} /></div>
            <div className="form-row">
              <div><label className="label">Destination</label>
                <input className="input" value={f.destination} onChange={(e) => setF({ ...f, destination: e.target.value })} /></div>
              <div><label className="label">Going with</label>
                <input className="input" placeholder="Father / uncle"
                  value={f.accompaniedBy} onChange={(e) => setF({ ...f, accompaniedBy: e.target.value })} /></div>
            </div>
            <div className="form-row">
              <div><label className="label">Leaving at</label>
                <input className="input" type="datetime-local" value={f.outAt}
                  onChange={(e) => setF({ ...f, outAt: e.target.value })} /></div>
              <div><label className="label">Due back</label>
                <input className="input" type="datetime-local" value={f.backAt}
                  onChange={(e) => setF({ ...f, backAt: e.target.value })} /></div>
            </div>
            <p style={{ fontSize: 12, color: 'var(--ink-3)' }}>
              If they aren't back by then, you and the parent are both alerted.
            </p>
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-accent" disabled={busy || !picked || !f.reason.trim() || !f.outAt || !f.backAt}
            onClick={save} style={{ marginLeft: 'auto' }}>
            {busy ? 'Saving…' : 'Request outpass'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------- rooms */

function Rooms() {
  const [blocks, setBlocks] = useState<Block[] | null>(null);
  const [adding, setAdding] = useState(false);
  const [allocating, setAllocating] = useState<{ bed: Bed; block: Block } | null>(null);

  const load = useCallback(() => {
    api.get<Block[]>('/api/hostel/blocks').then(setBlocks).catch(() => setBlocks([]));
  }, []);
  useEffect(load, [load]);

  if (!blocks) return <div className="card" style={{ padding: 20 }}><span className="sk" style={{ width: '50%' }} /></div>;

  if (blocks.length === 0) {
    return (
      <div className="empty">
        <h3>No blocks yet</h3>
        <p>Create a block — boys' or girls' — then add rooms and beds.</p>
        <button className="btn btn-accent" onClick={() => setAdding(true)}>+ Add block</button>
        {adding && <BlockDialog onClose={() => setAdding(false)} onDone={() => { setAdding(false); load(); }} />}
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', marginBottom: 14 }}>
        <button className="btn btn-accent" style={{ marginLeft: 'auto' }} onClick={() => setAdding(true)}>+ Add block</button>
      </div>

      {blocks.map((b) => (
        <div key={b.id} className="card" style={{ padding: 18, marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
            <h3 className="h-display" style={{ fontSize: 18 }}>{b.name}</h3>
            <span className="pill pill-muted" style={{ fontSize: 11 }}>{b.gender}</span>
            <span style={{ marginLeft: 'auto', fontSize: 13, color: 'var(--ink-2)' }}>
              <strong>{b.occupiedBeds}</strong> of {b.totalBeds} beds full
            </span>
            <button className="btn" style={{ padding: '4px 10px', fontSize: 12 }}
              onClick={async () => {
                const num = window.prompt('Room number?');
                if (!num) return;
                const beds = window.prompt('How many beds?', '4');
                if (!beds) return;
                await api.post(`/api/hostel/blocks/${b.id}/rooms`, {
                  roomNumber: num, bedCount: Number(beds), floor: null,
                }).catch(() => {});
                load();
              }}>
              + Room
            </button>
          </div>

          {b.rooms.length === 0 ? (
            <p style={{ fontSize: 13, color: 'var(--ink-3)' }}>No rooms yet.</p>
          ) : (
            <div className="grid-auto">
              {b.rooms.map((r) => (
                <div key={r.id} className="card" style={{ padding: 12, background: 'var(--surface-2)' }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 8 }}>
                    <strong>{r.roomNumber}</strong>
                    <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--ink-3)' }}>
                      {r.occupiedBeds}/{r.totalBeds}
                    </span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {r.beds.map((bed) => (
                      <button key={bed.id}
                        disabled={!!bed.occupantStudentId || !bed.isUsable}
                        onClick={() => setAllocating({ bed, block: b })}
                        style={{
                          display: 'flex', gap: 8, alignItems: 'center', padding: '6px 8px',
                          borderRadius: 6, fontSize: 12.5, textAlign: 'left',
                          border: '1px solid var(--line-2)',
                          background: bed.occupantStudentId ? 'var(--surface)' : 'transparent',
                          cursor: bed.occupantStudentId || !bed.isUsable ? 'default' : 'pointer',
                          color: bed.isUsable ? 'var(--ink)' : 'var(--ink-3)',
                        }}>
                        <span className="mono" style={{ fontSize: 11, color: 'var(--ink-3)' }}>{bed.label}</span>
                        <span style={{ marginLeft: 'auto' }}>
                          {!bed.isUsable ? 'out of service'
                            : bed.occupantName ?? <span style={{ color: 'var(--ok-ink, #1e7d4f)' }}>+ allocate</span>}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}

      {adding && <BlockDialog onClose={() => setAdding(false)} onDone={() => { setAdding(false); load(); }} />}
      {allocating && (
        <AllocateDialog bed={allocating.bed} block={allocating.block}
          onClose={() => setAllocating(null)}
          onDone={() => { setAllocating(null); load(); }} />
      )}
    </div>
  );
}

function BlockDialog({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [institutions, setInstitutions] = useState<{ id: string; name: string }[]>([]);
  const [f, setF] = useState({ institutionId: '', name: '', gender: 'Boys', note: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.get<{ id: string; name: string }[]>('/api/institutions')
      .then((r) => { setInstitutions(r); if (r.length === 1) setF((p) => ({ ...p, institutionId: r[0].id })); })
      .catch(() => {});
  }, []);

  const save = async () => {
    if (!f.institutionId || !f.name.trim()) return;
    setBusy(true); setError(null);
    try { await api.post('/api/hostel/blocks', { ...f, wardenUserId: null }); onDone(); }
    catch (e) { setError(e instanceof ApiError ? e.message : 'Could not save.'); }
    finally { setBusy(false); }
  };

  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <div className="sheet" onClick={(e) => e.stopPropagation()}>
        <h2 className="h-display" style={{ fontSize: 20, marginBottom: 14 }}>Add a block</h2>
        {error && <div className="pill pill-danger" style={{ padding: '8px 12px', borderRadius: 8, marginBottom: 12, justifyContent: 'flex-start' }}>{error}</div>}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {institutions.length > 1 && (
            <div><label className="label">Institution</label>
              <select className="input" value={f.institutionId} onChange={(e) => setF({ ...f, institutionId: e.target.value })}>
                <option value="">Select…</option>
                {institutions.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
              </select>
            </div>
          )}
          <div><label className="label">Block name</label>
            <input className="input" placeholder="Boys' Block A" value={f.name}
              onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
          <div>
            <label className="label">This block is for</label>
            <div style={{ display: 'flex', gap: 6 }}>
              {['Boys', 'Girls'].map((g) => (
                <button key={g} type="button" className={`chip ${f.gender === g ? 'is-on' : ''}`}
                  onClick={() => setF({ ...f, gender: g })}>{g}</button>
              ))}
            </div>
            <p style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 6 }}>
              This <strong>cannot be changed later</strong>. The system will refuse to allocate a
              student of the other gender to this block.
            </p>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-accent" disabled={busy || !f.name.trim()} onClick={save} style={{ marginLeft: 'auto' }}>
            {busy ? 'Saving…' : 'Add block'}
          </button>
        </div>
      </div>
    </div>
  );
}

function AllocateDialog({ bed, block, onClose, onDone }: {
  bed: Bed; block: Block; onClose: () => void; onDone: () => void;
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Resident[]>([]);
  const [picked, setPicked] = useState<Resident | null>(null);
  const [raiseFee, setRaiseFee] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (query.trim().length < 2) { setResults([]); return; }
    const t = setTimeout(() => {
      api.get<Resident[]>(`/api/hostel/students?search=${encodeURIComponent(query)}`)
        .then((r) => setResults(asList(r))).catch(() => setResults([]));
    }, 250);
    return () => clearTimeout(t);
  }, [query]);

  const save = async () => {
    if (!picked) return;
    setBusy(true); setError(null);
    try {
      await api.post('/api/hostel/allocate', {
        studentId: picked.studentId,
        bedId: bed.id,
        // The block's gender IS the required gender — the domain refuses a mismatch.
        studentGender: block.gender,
        note: null,
        raiseFee,
      });
      onDone();
    } catch (e) { setError(e instanceof ApiError ? e.message : 'Could not allocate.'); }
    finally { setBusy(false); }
  };

  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <div className="sheet" onClick={(e) => e.stopPropagation()}>
        <h2 className="h-display" style={{ fontSize: 19, marginBottom: 2 }}>Allocate bed {bed.label}</h2>
        <p style={{ color: 'var(--ink-2)', fontSize: 13, marginBottom: 14 }}>
          {block.name} · <strong>{block.gender}</strong> block
        </p>

        {error && <div className="pill pill-danger" style={{ padding: '9px 12px', borderRadius: 8, marginBottom: 12, justifyContent: 'flex-start' }}>{error}</div>}

        <label className="label">Student</label>
        <input className="input" value={picked ? picked.fullName : query}
          placeholder="Search…" onChange={(e) => { setPicked(null); setQuery(e.target.value); }} />

        {!picked && results.length > 0 && (
          <div className="card" style={{ marginTop: 4, padding: 0, overflow: 'hidden' }}>
            {results.map((r) => (
              <button key={r.studentId} onClick={() => { setPicked(r); setQuery(''); }}
                style={{
                  display: 'flex', gap: 8, width: '100%', padding: '9px 12px', textAlign: 'left',
                  background: 'none', border: 'none', borderBottom: '1px solid var(--line-2)', cursor: 'pointer',
                }}>
                <span>{r.fullName}</span>
                <span className="mono" style={{ fontSize: 11, color: 'var(--ink-3)', marginLeft: 'auto' }}>
                  {r.admissionNumber}
                </span>
              </button>
            ))}
          </div>
        )}

        {picked && (
          <>
            <div className="pill pill-warn" style={{
              padding: '10px 12px', borderRadius: 8, marginTop: 12, display: 'block',
            }}>
              You are confirming <strong>{picked.fullName}</strong> is <strong>{block.gender === 'Boys' ? 'male' : 'female'}</strong>.
              The system will refuse if this block doesn't match.
            </div>

            <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 12, fontSize: 14 }}>
              <input type="checkbox" checked={raiseFee} onChange={(e) => setRaiseFee(e.target.checked)} />
              Raise the hostel fee on their account
            </label>
          </>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-accent" disabled={busy || !picked} onClick={save} style={{ marginLeft: 'auto' }}>
            {busy ? 'Allocating…' : 'Allocate'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ----------------------------------------------------------------- visitors */

interface Visitor {
  id: string; studentId: string; studentName: string; visitorName: string;
  relation: string | null; mobile: string | null;
  inAt: string; outAt: string | null; isInside: boolean;
}

function Visitors() {
  const [rows, setRows] = useState<Visitor[] | null>(null);
  const [adding, setAdding] = useState(false);

  const load = useCallback(() => {
    setRows(null);
    api.get<Visitor[]>('/api/hostel/visitors').then(setRows).catch(() => setRows([]));
  }, []);
  useEffect(load, [load]);

  const inside = rows?.filter((v) => v.isInside).length ?? 0;

  const columns: Column<Visitor>[] = [
    {
      id: 'visitor', header: 'Visitor', primary: true, sortable: true,
      value: (v) => v.visitorName,
      cell: (v) => <>{v.visitorName}{v.relation && <span style={{ color: 'var(--ink-3)' }}> · {v.relation}</span>}</>,
    },
    {
      id: 'student', header: 'To see', sortable: true,
      value: (v) => v.studentName, cell: (v) => v.studentName,
    },
    {
      id: 'in', header: 'In', hideMd: true,
      value: (v) => v.inAt,
      cell: (v) => new Date(v.inAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    },
    {
      id: 'status', header: 'Status',
      value: (v) => v.isInside ? 'inside' : 'left',
      cell: (v) => v.isInside
        ? <span className="pill pill-warn" style={{ fontSize: 11 }}>Still inside</span>
        : <span className="pill pill-ok" style={{ fontSize: 11 }}>Left</span>,
    },
    {
      id: 'out', header: '', actions: true,
      cell: (v) => v.isInside ? (
        <button className="btn" style={{ padding: '4px 10px', fontSize: 12 }}
          onClick={async () => { await api.post(`/api/hostel/visitors/${v.id}/out`, {}).catch(() => {}); load(); }}>
          Sign out
        </button>
      ) : null,
    },
  ];

  return (
    <div>
      {inside > 0 && (
        <div className="pill pill-warn" style={{ padding: '9px 12px', borderRadius: 8, marginBottom: 12, justifyContent: 'flex-start' }}>
          <strong>{inside} visitor{inside === 1 ? '' : 's'} still inside the hostel.</strong>
        </div>
      )}

      <div style={{ display: 'flex', marginBottom: 12 }}>
        <button className="btn btn-accent" style={{ marginLeft: 'auto' }} onClick={() => setAdding(true)}>
          + Sign in a visitor
        </button>
      </div>

      <DataTable
        rows={rows}
        columns={columns}
        rowKey={(v) => v.id}
        search
        searchPlaceholder="Search visitors…"
        empty={{ title: 'No visitors today', body: 'Everyone who comes to see a student is recorded here.' }}
      />

      {adding && <VisitorDialog onClose={() => setAdding(false)} onDone={() => { setAdding(false); load(); }} />}
    </div>
  );
}

function VisitorDialog({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Resident[]>([]);
  const [picked, setPicked] = useState<Resident | null>(null);
  const [f, setF] = useState({ visitorName: '', relation: '', mobile: '', idProofNote: '' });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (query.trim().length < 2) { setResults([]); return; }
    const t = setTimeout(() => {
      api.get<Resident[]>(`/api/hostel/students?search=${encodeURIComponent(query)}`)
        .then((r) => setResults(asList(r))).catch(() => setResults([]));
    }, 250);
    return () => clearTimeout(t);
  }, [query]);

  const save = async () => {
    if (!picked || !f.visitorName.trim()) return;
    setBusy(true);
    try {
      await api.post('/api/hostel/visitors', { studentId: picked.studentId, ...f });
      onDone();
    } catch { /* surfaced by the list refresh */ }
    finally { setBusy(false); }
  };

  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <div className="sheet" onClick={(e) => e.stopPropagation()}>
        <h2 className="h-display" style={{ fontSize: 20, marginBottom: 14 }}>Sign in a visitor</h2>

        <label className="label">Here to see</label>
        <input className="input" value={picked ? picked.fullName : query}
          placeholder="Search student…" onChange={(e) => { setPicked(null); setQuery(e.target.value); }} />

        {!picked && results.length > 0 && (
          <div className="card" style={{ marginTop: 4, padding: 0, overflow: 'hidden' }}>
            {results.map((r) => (
              <button key={r.studentId} onClick={() => { setPicked(r); setQuery(''); }}
                style={{
                  display: 'block', width: '100%', padding: '9px 12px', textAlign: 'left',
                  background: 'none', border: 'none', borderBottom: '1px solid var(--line-2)', cursor: 'pointer',
                }}>
                {r.fullName}
              </button>
            ))}
          </div>
        )}

        {picked && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 12 }}>
            <div><label className="label">Visitor's name</label>
              <input className="input" value={f.visitorName}
                onChange={(e) => setF({ ...f, visitorName: e.target.value })} /></div>
            <div className="form-row">
              <div><label className="label">Relation</label>
                <input className="input" placeholder="Father / uncle"
                  value={f.relation} onChange={(e) => setF({ ...f, relation: e.target.value })} /></div>
              <div><label className="label">Mobile</label>
                <input className="input" value={f.mobile}
                  onChange={(e) => setF({ ...f, mobile: e.target.value })} /></div>
            </div>
            <div><label className="label">ID shown</label>
              <input className="input" placeholder="Aadhaar last 4 / driving licence"
                value={f.idProofNote} onChange={(e) => setF({ ...f, idProofNote: e.target.value })} /></div>
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-accent" disabled={busy || !picked || !f.visitorName.trim()}
            onClick={save} style={{ marginLeft: 'auto' }}>
            {busy ? 'Saving…' : 'Sign in'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */

function formatLate(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m === 0 ? `${h} hr` : `${h} hr ${m} min`;
}

function TabBtn({ on, onClick, children }: { on: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button onClick={onClick} style={{
      background: 'none', border: 'none', cursor: 'pointer', padding: '8px 14px', fontSize: 14,
      color: on ? 'var(--ink)' : 'var(--ink-3)', fontWeight: on ? 600 : 400,
      borderBottom: on ? '2px solid var(--brand)' : '2px solid transparent',
    }}>{children}</button>
  );
}
